/**
 * Printer Integration Models
 *
 * These models define the structure for printer configuration and management.
 * Stored in the backend as part of the "printers" integration settings.
 *
 * @see docs/INTEGRATIONS_BACKEND_IMPLEMENTATION.md for API contract
 */

/**
 * Connection method for a printer
 * Values must match backend API contract (uses underscores, not hyphens)
 */
export enum PrinterConnectionType {
  /** Manual IP address entry - works everywhere */
  MANUAL_IP = 'manual_ip',
  /** WebUSB connection - requires user gesture to pair */
  WEB_USB = 'web_usb',
  /** Bluetooth connection */
  BLUETOOTH = 'bluetooth',
  /** Network discovery via mDNS - only works in Isolated Web Apps */
  NETWORK_DISCOVERY = 'network_discovery',
}

/**
 * Printer protocol for sending commands
 */
export enum PrinterProtocol {
  /** Epson ESC/POS - most common for thermal printers */
  ESC_POS = 'esc-pos',
  /** Star Line Mode - Star Micronics printers */
  STAR_LINE = 'star-line',
}

/**
 * Paper width options for thermal printers
 * Values are numbers (mm) to match backend API contract
 */
export type PrinterPaperWidth = 58 | 80;

/** Paper width constants for type safety */
export const PAPER_WIDTH = {
  MM_58: 58 as const,
  MM_80: 80 as const,
};

/**
 * Character encoding for printer output
 */
export enum PrinterEncoding {
  /** UTF-8 encoding */
  UTF8 = 'utf-8',
  /** Windows-1252 (Western European) */
  CP1252 = 'cp1252',
  /** Code Page 437 (US) */
  CP437 = 'cp437',
  /** Code Page 850 (Multilingual Latin) */
  CP850 = 'cp850',
}

/**
 * Purpose/role for a printer
 */
export enum PrinterPurpose {
  /** Receipt printing after payment */
  RECEIPT = 'receipt',
  /** Kitchen order tickets */
  KITCHEN = 'kitchen',
  /** Bar order tickets */
  BAR = 'bar',
  /** Reports printing */
  REPORTS = 'reports',
}

/**
 * Connection status of a printer
 */
export enum PrinterConnectionStatus {
  /** Printer is connected and ready */
  CONNECTED = 'connected',
  /** Printer is disconnected */
  DISCONNECTED = 'disconnected',
  /** Currently connecting to printer */
  CONNECTING = 'connecting',
  /** Connection error occurred */
  ERROR = 'error',
  /** Status unknown (not yet checked) */
  UNKNOWN = 'unknown',
}

/**
 * Configuration for a single printer
 */
export interface IPrinterConfig {
  /** Unique identifier for this printer config */
  id: string;
  /** User-defined name/label for the printer */
  name: string;
  /** Connection method used */
  connectionType: PrinterConnectionType;
  /** Printer protocol */
  protocol: PrinterProtocol;
  /** Paper width */
  paperWidth: PrinterPaperWidth;
  /** Character encoding */
  encoding: PrinterEncoding;
  /** Purposes this printer serves */
  purposes: PrinterPurpose[];
  /** Whether this printer is enabled */
  isEnabled: boolean;

  // Connection settings - flat structure for simplicity
  /** For MANUAL_IP: IP address */
  ipAddress?: string;
  /** For MANUAL_IP: Port number (default 9100) */
  port?: number;
  /** For WEB_USB: Vendor ID */
  usbVendorId?: number;
  /** For WEB_USB: Product ID */
  usbProductId?: number;
  /** For WEB_USB: Serial number */
  usbSerialNumber?: string;

  /** Last known connection status */
  status?: PrinterConnectionStatus;
  /** Last error message if any */
  lastError?: string;
  /** Timestamp of last successful connection */
  lastConnected?: Date;
  /** Created timestamp */
  createdAt?: Date;
  /** Updated timestamp */
  updatedAt?: Date;
}

/**
 * Printer connection state (runtime only, not persisted)
 */
export interface IPrinterConnectionState {
  printerId: string;
  status: PrinterConnectionStatus;
  connectedAt?: Date;
  error?: string;
}

/**
 * Default printer assignments per purpose
 */
export interface IDefaultPrinters {
  [PrinterPurpose.RECEIPT]?: string;
  [PrinterPurpose.KITCHEN]?: string;
  [PrinterPurpose.BAR]?: string;
  [PrinterPurpose.REPORTS]?: string;
  // Dynamic keys for product types
  [key: string]: string | undefined;
}

/**
 * Complete printers integration settings
 * This is stored in the backend integration.settings field
 * @see docs/INTEGRATIONS_BACKEND_IMPLEMENTATION.md for API contract
 */
export interface IPrintersSettings {
  /** List of configured printers */
  printers: IPrinterConfig[];
  /** Default printer IDs per purpose */
  defaultPrinters: IDefaultPrinters;
  /** Global setting: enable tickets printing (replaces kitchen/bar) */
  enableTicketsPrinting: boolean;
  /** Global setting: print on order confirm */
  printOnOrderConfirm: boolean;
  /** Global setting: auto-cut after print */
  autoCut: boolean;
  /** Global setting: open cash drawer on receipt */
  openCashDrawer: boolean;
  /** Global setting: use compact layout for receipts and tickets */
  compactMode: boolean;
  /** Global setting: split kitchen/bar tickets by product type (separate tickets for drinks and food) */
  splitTicketsByProductType: boolean;
  /**
   * Override locale for printed content (e.g., 'en', 'ro', 'de').
   * If not set, the application's current language is used.
   * Useful when staff uses one language but receipts/tickets should be in another.
   */
  printLocale?: string;
}

/**
 * Default settings for new printers integration
 * Values match backend API defaults from INTEGRATIONS_BACKEND_IMPLEMENTATION.md
 */
export const DEFAULT_PRINTERS_SETTINGS: IPrintersSettings = {
  printers: [],
  defaultPrinters: {},
  enableTicketsPrinting: false,
  printOnOrderConfirm: false,
  autoCut: true,
  openCashDrawer: false,
  compactMode: true,
  splitTicketsByProductType: false,
};

/**
 * Default configuration for a new printer
 */
export const DEFAULT_PRINTER_CONFIG: Omit<IPrinterConfig, 'id' | 'name'> = {
  connectionType: PrinterConnectionType.MANUAL_IP,
  protocol: PrinterProtocol.ESC_POS,
  paperWidth: PAPER_WIDTH.MM_80,
  encoding: PrinterEncoding.UTF8,
  purposes: [PrinterPurpose.RECEIPT],
  isEnabled: true,
  port: 9100,
  status: PrinterConnectionStatus.UNKNOWN,
};

/**
 * Network discovered printer (mDNS)
 */
export interface IDiscoveredNetworkPrinter {
  /** IP address */
  address: string;
  /** Port number */
  port: number;
  /** Printer name from mDNS */
  name?: string;
  /** Discovery protocol used */
  discoveryProtocol: 'mdns' | 'ssdp';
  /** Timestamp when discovered */
  lastSeen: Date;
}

/**
 * Print job status
 */
export enum PrintJobStatus {
  PENDING = 'pending',
  PRINTING = 'printing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Print job in the queue
 */
export interface IPrintJob {
  /** Unique job ID */
  id: string;
  /** Printer ID to use */
  printerId: string;
  /** Printer name for display */
  printerName: string;
  /** Raw ESC/POS data to send */
  data: Uint8Array;
  /** Human-readable description */
  description: string;
  /** Current status */
  status: PrintJobStatus;
  /** Created timestamp */
  createdAt: Date;
  /** Completed timestamp */
  completedAt?: Date;
  /** Error message if failed */
  error?: string;
}

/**
 * Text alignment for printing
 */
export enum TextAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

/**
 * Receipt item structure
 */
export interface IReceiptItem {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
  variant?: string;
  modifiers?: string[];
  notes?: string;
}

/**
 * Receipt data structure
 */
export interface IReceiptData {
  storeName?: string;
  storeAddress?: string;
  storePhone?: string;
  orderNumber: string;
  date?: Date;
  printDate?: Date;
  tableNumber?: string;
  serverName?: string;
  items: IReceiptItem[];
  subtotal: number;
  tax?: number;
  taxRate?: number;
  discount?: number;
  discountLabel?: string;
  total: number;
  paymentMethod?: string;
  amountPaid?: number;
  change?: number;
  headerText?: string[];
  footerText?: string[];
  currency?: string;
  orderType: string;
  logoUrl?: string;
}

/**
 * Kitchen/Bar ticket item structure
 */
export interface IKitchenItem {
  name: string;
  quantity: number;
  variant?: string;
  modifiers?: string[];
  notes?: string;
  courseName?: string;
  isUrgent?: boolean;
  isServed?: boolean;
}

/**
 * Kitchen/Bar ticket data structure
 */
export interface IKitchenOrderData {
  orderNumber: string;
  /**
   * Optional suborder identifier (e.g. "R-1270").
   * When present, the ticket shows: "BON <suborder> / COMANDA <order>".
   * When multiple suborders exist, only the order number is shown.
   */
  suborderNumber?: string;
  /**
   * Optional list of all suborder IDs when printing aggregated tickets.
   * Used to determine if we should show individual BON ID or just COMANDA.
   */
  suborderNumbers?: string[];
  tableNumber?: string;
  serverName?: string;
  orderTime: Date;
  items: IKitchenItem[];
  destination: 'kitchen' | 'bar';
  notes?: string;
  orderType?: string;
  paymentMethod?: string;
}
